import { yupResolver } from '@hookform/resolvers/yup'
import {
  Alert,
  Box,
  Button,
  FormGroup,
  InputLabel,
  Paper,
  Rating,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'
import Lottie from 'lottie-react'
import React, { FC, ReactNode, useContext } from 'react'
import {
  Control,
  Controller,
  SubmitHandler,
  useForm,
  UseFormSetValue
} from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { CenteredLoading } from '../../components'
import ShopContactBox from '../../components/CustomerPortal/ShopContactBox'
import VehicleInfoBoxForCustomer from '../../components/SharedUICustomerPortal/VehicleInfoBoxForCustomer'
import { RepairOrderPortalContext } from '../../providers/RepairOrderPortalProvider/RepairOrderPortalProvider'
import {
  useGetApiKeyReviewQuery,
  useSubmitReviewMutation
} from '../../redux/api/feedbackApi'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import animation from './animation_lku9t797.json'

type NullableBinaryResponse = 'yes' | 'no' | ''

interface FormInputs {
  stars: number | null
  warranty: NullableBinaryResponse
  timely: NullableBinaryResponse
  courteous: NullableBinaryResponse
  informed: NullableBinaryResponse
  completed: NullableBinaryResponse
}

const BINARY_RESPONSE_DESCRIPTIONS: Record<string, string> = {
  warranty: 'Was your work covered by warranty?',
  timely: 'Was your vehicle received on time?',
  courteous: 'Was the staff courteous?',
  informed: 'Were you updated on the status of your vehicle?',
  completed: 'Was your vehicle fixed?'
}

const VALIDATION = yup.object().shape({
  stars: yup.number().required(),
  warranty: yup.string().required(),
  timely: yup.string().required(),
  courteous: yup.string().required(),
  informed: yup.string().required(),
  completed: yup.string().required()
})

interface ControlledBinaryToggleGroupProps {
  control: Control<FormInputs>
  setValue: UseFormSetValue<FormInputs>
  name: keyof FormInputs
}

const ControlledBinaryToggleGroup: FC<ControlledBinaryToggleGroupProps> = ({
  control,
  setValue,
  name
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <ToggleButtonGroup
        {...field}
        exclusive
        onChange={(event, value) => {
          setValue(field.name, value)
        }}>
        <ToggleButton value="yes" color="success">
          Yes
        </ToggleButton>
        <ToggleButton value="no" color="error">
          No
        </ToggleButton>
      </ToggleButtonGroup>
    )}
  />
)

const CustomerFeedback: FC = () => {
  document.title = 'InTune Auto - Customer Feedback'

  const { apiKey } = useParams()
  const { isLoading, shop, repairOrder, vehicle, vehicleImages } = useContext(
    RepairOrderPortalContext
  )
  const { data: review, isLoading: reviewLoading } = useGetApiKeyReviewQuery({
    xAPIKey: apiKey!
  })
  const [submitReview] = useSubmitReviewMutation()

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isSubmitting, errors }
  } = useForm<FormInputs>({
    defaultValues: {
      stars: null,
      warranty: '',
      timely: '',
      courteous: '',
      informed: '',
      completed: ''
    },
    resolver: yupResolver(VALIDATION)
  })

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    await submitReview({
      xAPIKey: apiKey!,
      customerFeedbackInfo: {
        warranty: data.warranty === 'yes',
        timely: data.timely === 'yes',
        courteous: data.courteous === 'yes',
        informed: data.informed === 'yes',
        completed: data.completed === 'yes',
        stars: data.stars!
      }
    })
  }

  const getBody: () => ReactNode = () => {
    if (isLoading || reviewLoading) {
      return <CenteredLoading />
    }
    if (review?.dateTime) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Stack spacing={2} padding={2} height="100%" maxWidth={'md'}>
            <Lottie
              animationData={animation}
              loop={true}
              style={{ height: '50vh' }}
            />
            <Typography variant="h1" textAlign="center">
              Thank you for your feedback!
            </Typography>
            {shop?.info?.googleMapsUrl && (
              <Button
                variant="contained"
                onClick={() => window.open(shop?.info?.googleMapsUrl)}>
                Review Us on Google
              </Button>
            )}
            {shop?.info?.yelpUrl && (
              <Button
                variant="contained"
                onClick={() => window.open(shop?.info?.yelpUrl)}>
                Review Us on Yelp
              </Button>
            )}
          </Stack>
        </div>
      )
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Stack spacing={2} padding={2} maxWidth={'md'}>
          <Typography variant="h1">Rate Your Experience</Typography>
          <ShopContactBox shop={shop!} />
          <VehicleInfoBoxForCustomer
            vehicleImages={vehicleImages!}
            vehicle={vehicle!}
          />

          <form onSubmit={handleSubmit(onSubmit)}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                boxSizing: 'border-box'
              }}>
              <Typography variant="h2">
                Please review your visit to {shop!.info!.name!} on{' '}
                {formatMdyDate(repairOrder!.entry!.created!)}
              </Typography>
              <Typography color="text.secondary">
                All responses are required
              </Typography>

              <Stack spacing={1}>
                {Object.keys(BINARY_RESPONSE_DESCRIPTIONS).map((fieldName) => (
                  <Stack spacing={1} key={fieldName}>
                    <Typography>
                      {BINARY_RESPONSE_DESCRIPTIONS[fieldName]}
                    </Typography>
                    <ControlledBinaryToggleGroup
                      control={control}
                      setValue={setValue}
                      name={fieldName as keyof FormInputs}
                    />
                    {errors[fieldName as keyof FormInputs]?.message && (
                      <Alert color="error">This field is required.</Alert>
                    )}
                  </Stack>
                ))}
                <FormGroup
                  sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <InputLabel
                    sx={{ textAlign: 'center', fontSize: 'h2.fontSize' }}>
                    Overall rating
                  </InputLabel>
                  <Controller
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Rating
                        size="large"
                        onChange={onChange}
                        value={
                          Number.isNaN(Number(value)) ? null : Number(value)
                        }
                        name="stars"
                      />
                    )}
                    name="stars"
                  />
                  {errors.stars?.message && (
                    <Alert color="error">Please submit a rating.</Alert>
                  )}
                </FormGroup>
              </Stack>
            </Paper>

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              sx={{ width: '100%', mt: 2 }}>
              {isSubmitting ? 'Sending...' : 'Submit'}
            </Button>
          </form>
        </Stack>
      </div>
    )
  }

  return <Box>{getBody()}</Box>
}

export default CustomerFeedback
